import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export function useDrinkLogsRealtime(groupId: string | null) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const subscribedGroupRef = useRef<string | null>(null);

  useEffect(() => {
    if (!groupId) {
      subscribedGroupRef.current = null;
      return;
    }

    // Prevent duplicate subscriptions for same group
    if (subscribedGroupRef.current === groupId) return;
    subscribedGroupRef.current = groupId;

    const channel = supabase
      .channel(`drink-logs-${groupId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'drink_logs',
          filter: `group_id=eq.${groupId}`,
        },
        (payload) => {
          // Skip invalidation if current user triggered the event
          // (mutations already invalidate queries on success)
          const newRecord = payload.new as { user_id?: string };
          if (newRecord.user_id === user?.id) return;

          queryClient.invalidateQueries({ queryKey: ['leaderboard', groupId] });
          queryClient.invalidateQueries({ queryKey: ['drink-logs'] });
          queryClient.invalidateQueries({ queryKey: ['user-streak'] });
          queryClient.invalidateQueries({ queryKey: ['challenge-drink-logs'] });
          queryClient.invalidateQueries({ queryKey: ['all-drink-logs'] });
          queryClient.invalidateQueries({ queryKey: ['personal-streak'] });
        }
      )
      .subscribe();

    return () => {
      subscribedGroupRef.current = null;
      supabase.removeChannel(channel);
    };
  }, [groupId, user?.id, queryClient]);
}
